import {
  AllRestaurantReportsResponse,
  IndividualRestaurantReportResponse,
  DailyOrderChartData,
  WeeklyRevenueChartData,
  MonthlyRevenueChartData,
  CategoryBreakdownChartData,
  RestaurantFilterOption,
} from "../../../types/restaurantReports";

// Transform daily order volume data for charts
export const transformDailyOrderData = (
  dailyOrderVolume: Record<string, number>
): DailyOrderChartData[] => {
  return Object.entries(dailyOrderVolume)
    .map(([date, orders]) => ({
      date,
      orders,
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

// Transform weekly revenue data for charts
export const transformWeeklyRevenueData = (
  weeklyRevenue: Record<string, number>
): WeeklyRevenueChartData[] => {
  return Object.entries(weeklyRevenue)
    .map(([week, revenue]) => ({
      week,
      revenue,
    }))
    .sort((a, b) => a.week.localeCompare(b.week));
};

// Transform monthly revenue data for charts
export const transformMonthlyRevenueData = (
  monthlyRevenue: Record<string, number>
): MonthlyRevenueChartData[] => {
  return Object.entries(monthlyRevenue)
    .map(([month, revenue]) => ({
      month,
      revenue,
    }))
    .sort((a, b) => a.month.localeCompare(b.month));
};

// Transform category breakdown data for charts
export const transformCategoryBreakdownData = (
  categoryBreakdown: Record<string, number>,
  totalOrders: number
): CategoryBreakdownChartData[] => {
  return Object.entries(categoryBreakdown)
    .map(([category, value]) => ({
      category,
      value,
      percentage: totalOrders > 0 ? (value / totalOrders) * 100 : 0,
    }))
    .sort((a, b) => b.value - a.value);
};

// Create restaurant filter options from reports data
export const createRestaurantFilterOptions = (
  restaurantReports: AllRestaurantReportsResponse["restaurantReports"]
): RestaurantFilterOption[] => {
  const options: RestaurantFilterOption[] = [
    { label: "All Restaurants", value: "all" },
  ];
  
  restaurantReports.forEach((restaurant) => {
    options.push({
      label: restaurant.restaurantName,
      value: restaurant.restaurantId,
      restaurantId: restaurant.restaurantId,
    });
  });
  
  return options;
};

// Format currency for display
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format numbers with commas
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat("en-US").format(num);
};

// Format percentage
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

// Calculate growth percentage
export const calculateGrowthPercentage = (
  current: number,
  previous: number
): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

// Get date range display text
export const getDateRangeText = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  const formatOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  
  return `${start.toLocaleDateString("en-US", formatOptions)} - ${end.toLocaleDateString("en-US", formatOptions)}`;
};

// Convert daily order volume to chart format expected by existing components
export const convertToMockDailyOrderData = (
  dailyOrderVolume: Record<string, number>
): Array<{ day: string; orders: number; revenue: number }> => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const result: Array<{ day: string; orders: number; revenue: number }> = [];
  
  // Get last 7 days from the data
  const sortedDates = Object.keys(dailyOrderVolume).sort();
  const lastSevenDates = sortedDates.slice(-7);
  
  lastSevenDates.forEach((date, index) => {
    const dayOfWeek = new Date(date).getDay();
    const orders = dailyOrderVolume[date] || 0;
    // Estimate revenue (in real implementation, you might have actual revenue data)
    const estimatedRevenue = orders * 1500; // Assuming average order value of ₦1500
    
    result.push({
      day: days[dayOfWeek],
      orders,
      revenue: estimatedRevenue,
    });
  });
  
  return result;
};

// Convert weekly revenue to chart format
export const convertToMockRevenueData = (
  weeklyRevenue: Record<string, number>
): Array<{ period: string; revenue: number; orders: number; averageOrderValue: number }> => {
  if (!weeklyRevenue || typeof weeklyRevenue !== 'object') {
    console.warn('Invalid weeklyRevenue data:', weeklyRevenue);
    return [];
  }
  
  return Object.entries(weeklyRevenue).map(([week, revenue]) => {
    // Ensure we have valid data
    const safeRevenue = typeof revenue === 'number' && !isNaN(revenue) ? revenue : 0;
    const safeWeek = typeof week === 'string' && week.trim() ? week : 'Unknown Week';
    
    // Estimate orders and average order value
    const estimatedOrders = Math.floor(safeRevenue / 1500);
    const averageOrderValue = estimatedOrders > 0 ? safeRevenue / estimatedOrders : 0;
    
    return {
      period: safeWeek, // Use the week key as-is since it's already "Week 1", "Week 2", etc.
      revenue: safeRevenue,
      orders: estimatedOrders,
      averageOrderValue,
    };
  }).filter(item => item.period !== 'Unknown Week'); // Filter out invalid entries
};

// Convert category breakdown to chart format (if we had category data)
export const convertToMockCategoryData = (
  categoryBreakdown: Record<string, number>
): Array<{ category: string; percentage: number; count: number; color: string }> => {
  const colors = ['#16a34a', '#f59e0b', '#8b5cf6', '#06b6d4', '#3b82f6'];
  const totalCount = Object.values(categoryBreakdown).reduce((sum, count) => sum + count, 0);
  
  if (totalCount === 0) {
    // Return default categories when no data
    return [
      { category: 'Rice Dishes', percentage: 32, count: 0, color: '#16a34a' },
      { category: 'Soups', percentage: 28, count: 0, color: '#f59e0b' },
      { category: 'Pastries', percentage: 25, count: 0, color: '#8b5cf6' },
      { category: 'Drinks', percentage: 15, count: 0, color: '#06b6d4' },
    ];
  }
  
  return Object.entries(categoryBreakdown).map(([category, count], index) => ({
    category,
    count,
    percentage: (count / totalCount) * 100,
    color: colors[index % colors.length],
  }));
};

// Get restaurant code from restaurant reports (this would typically come from a separate mapping)
// For now, we'll use a simple mapping function based on restaurant names
export const getRestaurantCode = (restaurantId: string, restaurantReports: AllRestaurantReportsResponse["restaurantReports"]): string => {
  // In a real implementation, you might have a lookup table or API endpoint
  // For the example API, it uses "FBCC716" for "Crispy Chicken"
  const restaurant = restaurantReports.find(r => r.restaurantId === restaurantId);
  if (restaurant) {
    // Generate a simple code based on restaurant name for demo
    // In production, this should come from the API or a proper mapping
    const name = restaurant.restaurantName.toUpperCase().replace(/\s+/g, '');
    return name.slice(0, 6) + Math.random().toString(36).slice(-3).toUpperCase();
  }
  return "FBCC716"; // Default fallback
};

// Time filter options
export const timeFilterOptions = [
  { label: "Last 7 Days", value: "last_7_days", days: 7 },
  { label: "Last 30 Days", value: "last_30_days", days: 30 },
  { label: "Last 3 Months", value: "last_3_months", days: 90 },
  { label: "Last 6 Months", value: "last_6_months", days: 180 },
  { label: "Last Year", value: "last_year", days: 365 },
];
